import type {PickerSize, PickerStatus} from '../../utils/pickerShellStyles';
import type {Locale} from 'antd/es/locale';
import type {ComponentProps, ReactNode} from 'react';

import {cn} from '@ds/helpers';
import {DatePicker as AntdJalaliDatePicker, JalaliLocaleListener} from 'antd-jalali';
import {jalaliLocale} from '../../locale';
import {useId} from 'react';

import {mergePickerPopupStyles} from '../../utils/pickerPopup';
import {PickerFieldShell, pickerShellClasses} from '../../utils/pickerShellStyles';
import {AntdDatePickerProvider} from '../AntdDatePickerProvider';

export type DatePickerProps = ComponentProps<typeof AntdJalaliDatePicker> & {
  wrapperClassName?: string;
  locale?: Locale;
  label?: ReactNode;
  required?: boolean;
  help?: ReactNode;
  size?: PickerSize;
  status?: PickerStatus;
};

export function DatePicker({
  wrapperClassName,
  locale = jalaliLocale,
  className,
  label,
  required,
  help,
  size = 'lg',
  status,
  id: idProp,
  styles,
  ...props
}: DatePickerProps) {
  const autoId = useId();
  const id = idProp ?? autoId;

  const picker = (
    <AntdDatePickerProvider locale={locale}>
      <JalaliLocaleListener />
      <div className={cn(pickerShellClasses(size, status), wrapperClassName)}>
        <AntdJalaliDatePicker
          id={id}
          className={cn('w-full', className)}
          styles={mergePickerPopupStyles(styles)}
          {...props}
        />
      </div>
    </AntdDatePickerProvider>
  );

  if (!label && !help) return picker;

  return (
    <PickerFieldShell id={id} label={label} required={required} help={help}>
      {picker}
    </PickerFieldShell>
  );
}

export default DatePicker;
